import React, { useCallback, useState } from 'react';

import {
  Box,
  Center,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { convertToMinutes, WithResourceIcon } from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import {
  CancelButton,
  PrimaryButton,
  textFieldStyles,
} from 'components/DataEntry';
import { AutomationIcon } from 'components/Icons';
import { RemoveIcon } from 'components/Icons/ReactResourceIcons/RemoveIcon';
import { Modal } from 'components/Overlay';
import { infoToast, successToast } from 'components/Toast';
import { OrgConfigBox } from 'containers/Settings/Components/OrganizationConfig/OrgConfigBox';
import { formatSlaLabel } from 'containers/Settings/Components/OrganizationConfig/utils';
import { selectSettings } from 'containers/Settings/selectors';
import { actions } from 'containers/Settings/slice';

interface IAvailableSLAsModal {
  isOpen: boolean;
  onClose: () => void;
  getAvailableSLAOptions: () => void;
  availableSlas: Record<string, any>[];
}

export const AvailableSLAsModal = (props: IAvailableSLAsModal) => {
  const { isOpen, onClose, getAvailableSLAOptions, availableSlas } = props;
  const [days, setDays] = useState(NaN);
  const [hours, setHours] = useState(NaN);
  const dispatch = useDispatch();
  const { updatePredefinedSlas, availableSLAs } = useSelector(selectSettings);

  const doUpdatePredefinedSlas = useCallback(
    (minutes: number, label) => {
      dispatch(
        actions.updateAvailableSlaOption({
          q: {
            availableSLACreateRequest: {
              minutes,
              display_label: label,
            },
          },
          onSuccess: () => {
            successToast({
              title: 'Updated SLAs successfully',
            });
            getAvailableSLAOptions();
          },
        }),
      );
    },
    [dispatch, getAvailableSLAOptions],
  );

  const addSlaOption = useCallback(
    (minutes: number, label: string) => {
      if (availableSlas.some(sla => sla.value === minutes)) {
        console.log('SLA already exists');
        infoToast({
          title: 'SLA already exists',
        });
        return;
      }
      doUpdatePredefinedSlas(minutes, label);
      setDays(NaN);
      setHours(NaN);
    },
    [availableSlas, doUpdatePredefinedSlas],
  );

  const removeSlaOption = useCallback(
    (sla: number) => {
      dispatch(
        actions.deleteAvailableSlaOption({
          q: { minutes: sla },
          onSuccess: () => {
            getAvailableSLAOptions();
          },
        }),
      );
    },
    [dispatch, getAvailableSLAOptions],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <WithResourceIcon
          icon={<AutomationIcon />}
          color={customTheme.colors.blue[300]}
          iconSize="md"
          border={false}
        >
          <Box fontSize="xl" fontWeight={600}>
            Edit SLA for Issues
          </Box>
        </WithResourceIcon>
      }
      body={
        <Box>
          <Stack align="start" h="full" w="full" spacing={4}>
            <Text>Available SLAs</Text>
            <OrgConfigBox px={0} py={3} position="relative" w="full" h="full">
              {availableSLAs.isLoading ? (
                <Center h={64}>
                  <CustomSpinner />
                </Center>
              ) : (
                <Stack
                  flex={1}
                  overflow={'auto'}
                  h={'full'}
                  w={'full'}
                  spacing={1}
                >
                  {availableSlas.map(sla => {
                    return (
                      // Fixed: key each SLA row (React missing-key warning).
                      <HStack
                        key={sla.value}
                        justify={'space-between'}
                        _hover={{ bg: '#eee' }}
                        py={1}
                        px={4}
                      >
                        <Text color={customTheme.colors.gray[250]}>
                          {sla.label}
                        </Text>
                        <IconButton
                          icon={<RemoveIcon />}
                          aria-label={''}
                          size={'xs'}
                          bg={'transparent'}
                          onClick={() => removeSlaOption(sla.value)}
                          isDisabled={sla?.data?.created_by === 'system'}
                        />
                      </HStack>
                    );
                  })}
                </Stack>
              )}

              <HStack mt={2} px={4}>
                <Input
                  placeholder={'Days'}
                  size="sm"
                  {...textFieldStyles.input}
                  type="number"
                  defaultValue={undefined}
                  // Fixed: show an empty field instead of passing NaN (React
                  // warns on NaN values) while no number is entered.
                  value={isNaN(days) ? '' : days}
                  onChange={e => {
                    const value = parseInt(e.target.value);
                    setDays(value);
                  }}
                />
                <Input
                  placeholder={'Hours'}
                  size="sm"
                  {...textFieldStyles.input}
                  type="number"
                  // Fixed: empty field instead of a NaN value, as for days.
                  value={isNaN(hours) ? '' : hours}
                  onChange={e => {
                    const value = parseInt(e.target.value);
                    setHours(value);
                  }}
                />
                {/*<AddButton
                  w={'120px'}
                  px={6}
                  onClick={() => {
                    if (days === 0 && hours === 0) return;
                    addSlaOption(
                      (isNaN(days) ? 0 : days * 24) +
                        (isNaN(hours) ? 0 : hours),
                    );
                  }}
                  isLoading={updatePredefinedSlas.isLoading}
                />*/}
              </HStack>
            </OrgConfigBox>
          </Stack>
        </Box>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton onClick={onClose} />
          <PrimaryButton
            px={6}
            isDisabled={!days && !hours}
            isLoading={updatePredefinedSlas.isLoading}
            onClick={() => {
              addSlaOption(
                convertToMinutes(days, 'days') +
                  convertToMinutes(hours, 'hours'),
                // Fixed: label was built as "1 days,2 hours"; now singular
                // units and a spaced separator ("1 day, 2 hours").
                formatSlaLabel(days, hours),
              );
            }}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
